import { useEffect } from "react";
import { type SelectedWorkbookExport } from "@/core/components/workbook-export";
import { toast } from "@volca/ui/lib/toast";
import { useDownloadAiScriptWorkbook } from "@/payroll/ai-script/query";
import {
  DEFAULT_AI_SCRIPT_WORKBOOK_FILENAME,
  getAiScriptWorkbookDownloadErrorMessage,
} from "@/payroll/ai-script/utils";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type UseAiWorkbookDownloadParams = {
  businessId: string | null;
} & Pick<PayBreakdownPeriodState, "output" | "generatedWorkbookMetadata">;

export const useAiWorkbookDownload = ({
  businessId,
  output,
  generatedWorkbookMetadata,
}: UseAiWorkbookDownloadParams) => {
  const {
    download: downloadAiWorkbook,
    isDownloading: isDownloadingAiWorkbook,
    isPreparing: isPreparingAiWorkbook,
    error: aiWorkbookDownloadError,
    isComplete: isAiWorkbookDownloadComplete,
  } = useDownloadAiScriptWorkbook(businessId);

  useEffect(() => {
    if (!isPreparingAiWorkbook) return;

    toast.info("Preparing your Grid", {
      description:
        "Large payrolls take a moment to build. The download starts on its own.",
    });
  }, [isPreparingAiWorkbook]);

  useEffect(() => {
    if (!aiWorkbookDownloadError) return;

    toast.error("Failed to download payroll result", {
      description: getAiScriptWorkbookDownloadErrorMessage(
        aiWorkbookDownloadError,
      ),
    });
  }, [aiWorkbookDownloadError]);

  const handleDownloadAiWorkbook = (options?: SelectedWorkbookExport) => {
    if (output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) return;

    const filename =
      generatedWorkbookMetadata?.filename ??
      DEFAULT_AI_SCRIPT_WORKBOOK_FILENAME;
    downloadAiWorkbook({
      runId: output.runId,
      filename: options
        ? filename.replace(/\.xlsx$/i, `.${options.exportFormat}`)
        : filename,
      ...options,
    });
  };

  return {
    isDownloadingAiWorkbook,
    isPreparingAiWorkbook,
    aiWorkbookDownloadError,
    isAiWorkbookDownloadComplete,
    handleDownloadAiWorkbook,
  };
};
